import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'

import CustomTooltip from 'pages/parts/CustomTooltip';
import HintIcon from 'pages/parts/HintIcon'
import { httpOk, GET, POST, PUT } from 'helpers/api'
import { copyText, formatDate, formatMoney, formatMoneyInput, parseLocalDateInput } from "helpers/helpers"

// Тип правила: 6 — персональные коды по SMS, 7 — один общий код
const TYPE_PERSONAL = 6
const TYPE_GENERAL = 7

// Те же алфавиты, что у генератора на бэке (PromoCodeController::SUFFIX_ALPHABETS):
// без I/O и 0/1, которые гость путает при вводе с SMS
const SUFFIX_ALPHABETS = {
	1: '0123456789',
	2: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
	3: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
}

function onlyDigits(value) {
	return (value ?? '').replace(/[^0-9]/g, '')
}

// Канон кода: латиница и цифры в верхнем регистре, без пробелов и знаков
function normalizeCode(value) {
	return (value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
}

const emptyItem = { 'discount_payment_type': 2, 'symbol_type_id': 3, 'suffix_length': 6 }

function Index() {
	const { t } = useTranslation()

	const symbolTypes = [
		{ 'id': 1, 'name': t('promo_symbols_digits') },
		{ 'id': 2, 'name': t('promo_symbols_letters') },
		{ 'id': 3, 'name': t('promo_symbols_mixed') },
	]
	const [data, setData] = useState([])
	const [showModal, setShowModal] = useState(false)
	const [showModalPromoCodeStatus, setShowModalPromoCodeStatus] = useState(false)
	const [selectedItem, setSelectedItem] = useState(emptyItem)
	const [discountTypes, setDiscountTypes] = useState([])
	const [smsTemplates, setSmsTemplates] = useState([])
	const [orderTypeSubs, setOrderTypeSubs] = useState([])
	const [isSmsCatalog, setIsSmsCatalog] = useState(false)

	const isEdit = !!selectedItem.id
	const isPercent = Number(selectedItem.discount_payment_type) === 2

	// Пример кода пересобирается только при смене префикса, длины или алфавита, а не на каждый ввод
	const exampleCode = useMemo(() => {
		const alphabet = SUFFIX_ALPHABETS[selectedItem.symbol_type_id] ?? SUFFIX_ALPHABETS[3]
		const length = Math.min(Math.max(Number(selectedItem.suffix_length) || 0, 0), 7)
		let suffix = ''
		for (let i = 0; i < length; i++) {
			suffix += alphabet[Math.floor(Math.random() * alphabet.length)]
		}
		return (selectedItem.prefix ?? '') + suffix
	}, [selectedItem.prefix, selectedItem.suffix_length, selectedItem.symbol_type_id])

	async function createUpdate(e) {
		e.preventDefault()

		var response
		if (selectedItem.id) {
			response = await PUT('/discount/promo-codes', selectedItem, { loader: true })
		} else {
			response = await POST('/discount/promo-codes', selectedItem, { loader: true })
		}

		if (httpOk(response)) {
			toggleModal(false)
			getData()
		}
	}

	async function updatePromoCodeStatus(e) {
		e.preventDefault()

		var response = await PUT('/discount/promo-codes/status', selectedItem, { loader: true })

		if (httpOk(response)) {
			toggleModalPromoCodeStatus(false)
			getData()
		}
	}

	function toggleModal(bool, item = emptyItem) {
		// Условия (дни/время/тип заказа) — массивы по умолчанию, значения правила перекрывают
		setSelectedItem({ days: [], order_type_subs: [], ...item })
		setShowModal(bool)
	}

	// Дни и время условия — как в карточке скидки (CreateUpdateCheque)
	function addTime() {
		setSelectedItem({
			...selectedItem,
			days: [...(selectedItem.days ?? []), {
				start_time: '', end_time: '',
				day1: false, day2: false, day3: false, day4: false, day5: false, day6: false, day7: false,
			}],
		})
	}

	function handleChangeTime(e, index, type) {
		const days = [...(selectedItem.days ?? [])]
		days[index] = { ...days[index], [type]: e.target.value }
		setSelectedItem({ ...selectedItem, days })
	}

	function setDayStatus(index, key) {
		const days = [...(selectedItem.days ?? [])]
		days[index] = { ...days[index], [key]: !days[index][key] }
		setSelectedItem({ ...selectedItem, days })
	}

	function deleteDay(index) {
		const days = [...(selectedItem.days ?? [])]
		days.splice(index, 1)
		setSelectedItem({ ...selectedItem, days })
	}

	function toggleModalPromoCodeStatus(bool, item = {}) {
		if (bool) {
			var sendData = {}
			sendData.id = item.id
			sendData.status = item.status
			sendData.delete_all = false
			setSelectedItem(sendData)
		} else {
			setSelectedItem(item)
		}

		setShowModalPromoCodeStatus(bool)
	}

	function setDiscountPaymentType(type) {
		if (isEdit) return
		setSelectedItem({ ...selectedItem, 'discount_payment_type': type })
	}

	function handleDiscountValue(value) {
		var digits = onlyDigits(value)
		// Процент не может быть больше 100 — не даём набрать 150, чем ловить ошибку после «Сохранить»
		if (isPercent && Number(digits) > 100) digits = '100'
		setSelectedItem({ ...selectedItem, 'discount_payment_value': digits })
	}

	function returnDiscountText(item) {
		if (Number(item.discount_payment_type) === 2) {
			return `${formatMoney(item.discount_payment_value)} %`
		}
		return `${formatMoney(item.discount_payment_value)} ${t('currency_soum')}`
	}

	// Название типа — по коду из discount_types.value, чтобы было на языке кабинета
	function returnTypeName(value, fallback) {
		return t('promo_type_' + value, { 'defaultValue': fallback ?? '' })
	}

	// Условия правила одной строкой для таблицы: от суммы, потолок, даты (лимит — в колонке «Использовано»)
	function returnConditions(item) {
		const parts = []
		if (item.min_order_amount) parts.push(t('promo_cond_from', { 'value': formatMoney(item.min_order_amount) }))
		if (item.max_discount_amount) parts.push(t('promo_cond_max', { 'value': formatMoney(item.max_discount_amount) }))
		if (item.starts_at) parts.push(t('promo_cond_since', { 'date': formatDate(item.starts_at) }))
		if (item.ends_at) parts.push(t('promo_cond_until', { 'date': formatDate(item.ends_at) }))
		return parts.join(' · ')
	}

	// Состояние правила для гостя: включено ли оно, идут ли даты, не кончился ли лимит.
	// Важнее флага «включено»: включённое правило с прошедшей датой код всё равно не примет
	function returnRuleStatus(item) {
		const today = formatDate(new Date(), 'yyyy-MM-dd')
		if (item.status === -1) return { 'text': t('promo_rule_disabled'), 'className': 'bg-danger text-white' }
		if (item.ends_at && String(item.ends_at).slice(0, 10) < today) {
			return { 'text': t('promo_rule_ended'), 'className': 'bg-secondary text-white' }
		}
		if (item.usage_limit && Number(item.total_activated) >= Number(item.usage_limit)) {
			return { 'text': t('promo_rule_limit_reached'), 'className': 'bg-secondary text-white' }
		}
		if (item.starts_at && String(item.starts_at).slice(0, 10) > today) {
			return { 'text': t('promo_rule_not_started'), 'className': 'bg-warning' }
		}
		// Персональный код без шаблона клиенту не уйдёт — это поломка, а не «активно»
		if (item.discount_type_id === TYPE_PERSONAL && !item.sms_template_id && !isSmsCatalog) {
			return { 'text': t('promo_no_sms_template'), 'className': 'bg-warning', 'title': t('promo_sms_template_missing') }
		}
		return { 'text': t('promo_rule_active'), 'className': 'bg-success text-white' }
	}

	function returnDiscountPreview() {
		if (!selectedItem.discount_payment_value) return null
		if (isPercent) {
			return t('promo_preview_percent', { value: selectedItem.discount_payment_value })
		}
		return t('promo_preview_amount', { value: formatMoney(selectedItem.discount_payment_value), currency: t('currency_soum') })
	}

	async function getData() {
		const response = await GET('/discount/promo-codes', {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	async function getDiscountTypes() {
		const response = await GET(`/helper/discount-types?type=promo_code`, {}, { loader: true })
		if (httpOk(response)) setDiscountTypes(response.data)
	}

	async function getSmsTemplates() {
		const response = await GET(`/helper/sms-templates?sms_type_id=2`, {}, { loader: true })
		if (httpOk(response)) setSmsTemplates(response.data)

		// Mison SMS: код уходит шаблоном каталога «Промокод», свой шаблон заведения не нужен
		const catalogResponse = await GET('/sms/catalog')
		if (httpOk(catalogResponse)) setIsSmsCatalog(!!catalogResponse.data?.is_template_only)
	}

	async function getOrderTypeSubs() {
		const response = await GET('/helper/order-type-subs', {}, {})
		if (httpOk(response)) setOrderTypeSubs(response.data)
	}

	useEffect(() => {
		getData()
		getDiscountTypes()
		getSmsTemplates()
		getOrderTypeSubs()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('promocodes')} [{data.length}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '22%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('promo_col_code')}</th>
									<th style={{ 'width': '8%' }} className="text-end">{t('discount')}</th>
									<th style={{ 'width': '20%' }}>{t('promo_conditions')}</th>
									<th style={{ 'width': '8%' }} className="text-end">{t('promo_issued')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('promo_used')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('created')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center"
											onClick={() => copyText(item.id)}>
											<CustomTooltip text={item.id}>
												{index + 1}
											</CustomTooltip>
										</td>
										{/* Название открывает правку — самое частое действие со строкой */}
										<td>
											<span className="text-primary fw-500 cursor" role="button"
												onClick={() => toggleModal(true, item)}>
												{item.name}
											</span>
											<div className="text-muted fz-12">
												{returnTypeName(item.discount_type_value, item.discount_type_name)}
											</div>
										</td>
										<td className="text-center text-nowrap">
											{item.discount_type_id === TYPE_GENERAL ?
												<b>{item.promo_code}</b>
												:
												<span title={t('promo_code_mask_hint')}>
													<b>{item.prefix}</b>
													<span className="text-muted">{'•'.repeat(Number(item.suffix_length) || 0)}</span>
												</span>
											}
										</td>
										<td className="text-end text-nowrap">{returnDiscountText(item)}</td>
										<td className="fz-12">{returnConditions(item)}</td>
										{/* Цифры ведут в отчёт по правилу */}
										<td className="text-end">
											<Link to={`/discount/promo-codes/${item.id}`}>
												{formatMoney(item.total_created)}
											</Link>
										</td>
										<td className="text-end text-nowrap">
											<Link to={`/discount/promo-codes/${item.id}`}>
												{formatMoney(item.total_activated)}
												{item.usage_limit ? ` / ${formatMoney(item.usage_limit)}` : ''}
											</Link>
										</td>
										<td className={`text-center ${returnRuleStatus(item).className}`}
											title={returnRuleStatus(item).title ?? ''}>
											{returnRuleStatus(item).text}
										</td>
										<td className="text-center">
											{formatDate(item.created_at)}
											<div className="text-muted fz-12">{item.created_by}</div>
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModal(true, item)}>
															<i className="uil-edit-alt"></i>
															{t('edit')}
														</div>
														<Link className="table-dropdown-item" to={`/discount/promo-codes/${item.id}`}>
															<i className="uil-chart-bar"></i>
															{t('report')}
														</Link>
														{item.status === 1 &&
															<div className="table-dropdown-item text-danger"
																onClick={() => toggleModalPromoCodeStatus(true, item)}>
																<i className="uil-times"></i>
																{t('promo_turn_off')}
															</div>
														}
														{item.status === -1 &&
															<div className="table-dropdown-item text-success"
																onClick={() => toggleModalPromoCodeStatus(true, item)}>
																<i className="uil-check"></i>
																{t('promo_turn_on')}
															</div>
														}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
								{data.length === 0 &&
									<tr>
										<td colSpan="10" className="text-center text-muted py-4">
											{t('promo_list_empty')}
										</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showModal} animation={false} centered scrollable backdrop="static"
				onHide={() => toggleModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{!selectedItem.id ?
							<>{t('create')}</>
							:
							<>{t('edit')}</>
						}
					</Modal.Title>
				</Modal.Header>
				<form onSubmit={createUpdate} autoComplete="off" className="d-flex flex-column" style={{ 'minHeight': 0 }}>
					<Modal.Body style={{ 'height': '500px' }}>
						<div className="form-group">
							<label>{t('type')}<span className="required-mark">*</span></label>
							<Select
								isDisabled={isEdit}
								options={discountTypes}
								value={discountTypes.find(option => option.id === selectedItem.discount_type_id) || ''}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'discount_type_id': option.id })}
								placeholder={t('promo_type_placeholder')}
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => returnTypeName(option.value, option.name)}
								getOptionValue={(option) => option.id}
							/>
							{selectedItem.discount_type_id === TYPE_PERSONAL &&
								<div className="text-muted fz-12 mt-1">{t('promo_type_personal_hint')}</div>
							}
							{selectedItem.discount_type_id === TYPE_GENERAL &&
								<div className="text-muted fz-12 mt-1">{t('promo_type_general_hint')}</div>
							}
						</div>

						{selectedItem.discount_type_id &&
							<>
								<div className="form-group">
									<label>
										{t('name2')}<span className="required-mark">*</span>
										<HintIcon text={t('promo_name_hint')} />
									</label>
									<input type="text" className="form-control" name="name" maxLength="255"
										placeholder={t('promo_name_placeholder')}
										value={selectedItem.name ?? ''}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
								</div>

								{selectedItem.discount_type_id === TYPE_GENERAL &&
									<div className="form-group">
										<label>
											{t('promo_code')}<span className="required-mark">*</span>
											<HintIcon text={t('promo_code_hint')} />
										</label>
										<input type="text" className="form-control text-uppercase" name="promo_code"
											maxLength="12"
											placeholder="AUTUMN2024"
											disabled={isEdit}
											value={selectedItem.promo_code ?? ''}
											onChange={(e) => setSelectedItem({ ...selectedItem, 'promo_code': normalizeCode(e.target.value) })} />
										<div className="text-muted fz-12 mt-1">{t('promo_code_format')}</div>
									</div>
								}

								{selectedItem.discount_type_id === TYPE_PERSONAL &&
									<>
										<div className="d-flex gap-2">
											<div className="form-group w-50">
												<label>
													{t('prefix')}<span className="required-mark">*</span>
													<HintIcon text={t('promo_prefix_hint')} />
												</label>
												<input type="text" className="form-control text-uppercase" name="prefix"
													maxLength="5"
													placeholder="VIP"
													disabled={isEdit}
													value={selectedItem.prefix ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'prefix': normalizeCode(e.target.value).slice(0, 5) })} />
											</div>
											<div className="form-group w-50">
												<label>
													{t('random_length')}<span className="required-mark">*</span>
													<HintIcon text={t('promo_suffix_length_hint')} />
												</label>
												<input type="number" className="form-control" name="suffix_length"
													min="4" max="7"
													disabled={isEdit}
													value={selectedItem.suffix_length ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'suffix_length': e.target.value.replace(/[^0-9]/g, '').slice(0, 1) })} />
											</div>
										</div>

										<div className="form-group">
											<label>{t('random_type')}<span className="required-mark">*</span></label>
											<Select
												isDisabled={isEdit}
												options={symbolTypes}
												value={symbolTypes.find(option => option.id === selectedItem.symbol_type_id) || ''}
												onChange={(option) => setSelectedItem({ ...selectedItem, 'symbol_type_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
											{!isEdit && selectedItem.prefix &&
												<div className="fz-12 mt-1">
													<span className="text-muted">{t('promo_code_example')}: </span>
													<b>{exampleCode}</b>
												</div>
											}
										</div>
									</>
								}

								<div className="form-group">
									<label>
										{t('promo_discount_size')}<span className="required-mark">*</span>
										<HintIcon text={isPercent ? t('promo_percent_hint') : t('promo_amount_hint')} />
									</label>
									<div className="d-flex gap-3 mb-2">
										<div className="form-check p-0">
											<div className="radio-round"
												onClick={() => setDiscountPaymentType(2)}
												style={{ 'backgroundColor': isPercent ? 'black' : '', 'cursor': isEdit ? 'default' : 'pointer' }}>
											</div>
											<span onClick={() => setDiscountPaymentType(2)}>
												{t('percentage')}
											</span>
										</div>
										<div className="form-check p-0">
											<div className="radio-round"
												onClick={() => setDiscountPaymentType(1)}
												style={{ 'backgroundColor': !isPercent ? 'black' : '', 'cursor': isEdit ? 'default' : 'pointer' }}>
											</div>
											<span onClick={() => setDiscountPaymentType(1)}>
												{t('amount')}
											</span>
										</div>
									</div>
									<div className="input-group">
										<input type="text" inputMode="numeric" className="form-control"
											name="discount_payment_value"
											placeholder={isPercent ? '10' : '20 000'}
											disabled={isEdit}
											value={formatMoneyInput(selectedItem.discount_payment_value) ?? ''}
											onChange={(e) => handleDiscountValue(e.target.value)} />
										<span className="input-group-text">{isPercent ? '%' : t('currency_soum')}</span>
									</div>
									<div className="text-muted fz-12 mt-1">
										{returnDiscountPreview() ?? (isPercent ? t('promo_percent_hint') : t('promo_amount_hint'))}
									</div>
									{isEdit &&
										<div className="text-muted fz-12 mt-1">{t('promo_locked_hint')}</div>
									}
								</div>

								{/* Условия применения: необязательные, меняются и после создания */}
								<div className="border-top pt-2 mt-3 mb-2">
									<div className="fw-600">{t('promo_conditions')}</div>
									<div className="text-muted fz-12">{t('promo_conditions_hint')}</div>
								</div>

								<div className="d-flex gap-2">
									<div className="form-group w-50">
										<label>
											{t('promo_min_order_amount')}
											<HintIcon text={t('promo_min_order_hint')} />
										</label>
										<div className="input-group">
											<input type="text" inputMode="numeric" className="form-control"
												placeholder="100 000"
												value={formatMoneyInput(selectedItem.min_order_amount) ?? ''}
												onChange={(e) => setSelectedItem({ ...selectedItem, 'min_order_amount': onlyDigits(e.target.value) })} />
											<span className="input-group-text">{t('currency_soum')}</span>
										</div>
									</div>
									<div className="form-group w-50">
										<label>
											{t('promo_usage_limit')}
											<HintIcon text={t('promo_usage_limit_hint')} />
										</label>
										<input type="text" inputMode="numeric" className="form-control"
											placeholder="100"
											value={formatMoneyInput(selectedItem.usage_limit) ?? ''}
											onChange={(e) => setSelectedItem({ ...selectedItem, 'usage_limit': onlyDigits(e.target.value) })} />
									</div>
								</div>

								{isPercent &&
									<div className="form-group">
										<label>
											{t('promo_max_discount')}
											<HintIcon text={t('promo_max_discount_hint')} />
										</label>
										<div className="input-group">
											<input type="text" inputMode="numeric" className="form-control"
												placeholder="50 000"
												value={formatMoneyInput(selectedItem.max_discount_amount) ?? ''}
												onChange={(e) => setSelectedItem({ ...selectedItem, 'max_discount_amount': onlyDigits(e.target.value) })} />
											<span className="input-group-text">{t('currency_soum')}</span>
										</div>
									</div>
								}

								<div className="d-flex gap-2">
									<div className="form-group w-50">
										<label>
											{t('promo_starts_at')}
											<HintIcon text={t('promo_period_hint')} />
										</label>
										<div className="calendar-input">
											<DatePicker
												className="form-control"
												dateFormat="dd.MM.yyyy"
												isClearable
												popperProps={{ 'strategy': 'fixed' }}
												selected={parseLocalDateInput(selectedItem.starts_at)}
												onChange={(date) => setSelectedItem({ ...selectedItem, 'starts_at': date ? formatDate(date, 'yyyy-MM-dd') : null })} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
									<div className="form-group w-50">
										<label>{t('promo_ends_at')}</label>
										<div className="calendar-input">
											<DatePicker
												className="form-control"
												dateFormat="dd.MM.yyyy"
												isClearable
												popperProps={{ 'strategy': 'fixed' }}
												minDate={parseLocalDateInput(selectedItem.starts_at) ?? (isEdit ? null : new Date())}
												selected={parseLocalDateInput(selectedItem.ends_at)}
												onChange={(date) => setSelectedItem({ ...selectedItem, 'ends_at': date ? formatDate(date, 'yyyy-MM-dd') : null })} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
								</div>

								{selectedItem.discount_type_id === TYPE_PERSONAL &&
									<>
										<div className="form-group">
											<label>
												{t('promo_validity_days')}<span className="required-mark">*</span>
												<HintIcon text={t('promo_validity_hint')} />
											</label>
											<div className="input-group">
												<input type="text" inputMode="numeric" className="form-control" name="validity_days"
													placeholder="30"
													value={selectedItem.validity_days ?? ''}
													onChange={(e) => setSelectedItem({ ...selectedItem, 'validity_days': e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })} />
												<span className="input-group-text">{t('days')}</span>
											</div>
										</div>

										{isSmsCatalog &&
											<div className="alert alert-info py-2 fz-12">{t('promo_sms_mison_catalog')}</div>
										}
										{!isSmsCatalog &&
											<div className="form-group">
												<label>
													SMS {t('template')}<span className="required-mark">*</span>
													<HintIcon text={t('promo_sms_template_hint')} />
												</label>
												<Select
													options={smsTemplates}
													value={smsTemplates.find(option => option.id === selectedItem.sms_template_id) || ''}
													onChange={(option) => setSelectedItem({ ...selectedItem, 'sms_template_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
												<div className="fz-12 mt-1">
													<span className="text-muted">{t('promo_sms_template_short')} </span>
													<Link to="/sms-templates" target="_blank">{t('promo_sms_templates_link')}</Link>
												</div>
											</div>
										}

										<div className="form-check">
											<input id="create_once_client" className="form-check-input" type="checkbox"
												disabled={isEdit}
												checked={selectedItem.create_once_client ?? false}
												onChange={(e) => setSelectedItem({ ...selectedItem, 'create_once_client': e.target.checked })}
											/>
											<label className="form-check-label" htmlFor="create_once_client">
												{t('promo_code_rule')}
											</label>
										</div>
									</>
								}

								{/* Условия движка акций: дни недели + время + тип заказа */}
								<div className="border-top pt-2 mt-3 mb-2">
									<div className="fw-600">{t('promo_schedule')}</div>
									<div className="text-muted fz-12">{t('promo_schedule_hint')}</div>
								</div>

								<div className="form-group">
									<label>{t('order_type')}</label>
									<Select
										isMulti
										value={selectedItem.order_type_subs ?? []}
										options={orderTypeSubs}
										onChange={(options) => setSelectedItem({ ...selectedItem, 'order_type_subs': options ?? [] })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>

								<div className="form-group">
									<div className="d-flex gap-2 mb-2">
										<label className="mb-0">{t('discount_day_time')}</label>
										<div className="table-action-button table-action-primary-button"
											onClick={() => addTime()}>
											<i className="uil-plus"></i>
										</div>
									</div>
									<div className="table-responsive">
										<table className="table table-bordered mb-0">
											<thead>
												<tr>
													<th>{t('start')}</th>
													<th>{t('end')}</th>
													<th className="text-center">{t('monday_short')}</th>
													<th className="text-center">{t('tuesday_short')}</th>
													<th className="text-center">{t('wednesday_short')}</th>
													<th className="text-center">{t('thursday_short')}</th>
													<th className="text-center">{t('friday_short')}</th>
													<th className="text-center">{t('saturday_short')}</th>
													<th className="text-center">{t('sunday_short')}</th>
													<th style={{ 'width': '40px' }}></th>
												</tr>
											</thead>
											<tbody>
												{selectedItem.days?.map((item, index) => (
													<tr key={index}>
														<td>
															<input type="time" className="form-control"
																value={(item.start_time || '').slice(0, 5)}
																onChange={(e) => handleChangeTime(e, index, 'start_time')} />
														</td>
														<td>
															<input type="time" className="form-control"
																value={(item.end_time || '').slice(0, 5)}
																onChange={(e) => handleChangeTime(e, index, 'end_time')} />
														</td>
														{[1, 2, 3, 4, 5, 6, 7].map((day) => (
															<td key={day} className="vertical-middle text-center">
																<input type="checkbox" className="form-check-input"
																	checked={item[`day${day}`] ?? false}
																	onChange={() => setDayStatus(index, `day${day}`)} />
															</td>
														))}
														<td>
															<div className="table-action-button table-action-danger-button"
																onClick={() => deleteDay(index)}>
																<i className="uil-times"></i>
															</div>
														</td>
													</tr>
												))}
												{(!selectedItem.days || selectedItem.days.length === 0) &&
													<tr>
														<td colSpan="10" className="text-center text-muted fz-12 py-2">
															{t('promo_schedule_empty')}
														</td>
													</tr>
												}
											</tbody>
										</table>
									</div>
								</div>
							</>
						}
					</Modal.Body>
					<Modal.Footer>
						<div className="d-flex gap-2 w-100">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!selectedItem.discount_type_id}>
								{t('save')}
							</button>
						</div>
					</Modal.Footer>
				</form>
			</Modal>
			{/* MODAL */}

			{/* MODAL PROMO CODE STATUS*/}
			<Modal show={showModalPromoCodeStatus} animation={false} centered
				onHide={() => toggleModalPromoCodeStatus(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedItem.status === 1 ?
							<span>{t('promo_turn_off')}</span>
							:
							<span>{t('promo_turn_on')}</span>
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={updatePromoCodeStatus} autoComplete="off">
						{selectedItem.status === 1 &&
							<>
								<p className="fz-14 text-muted">{t('promo_turn_off_hint')}</p>
								<div className="form-check mt-2">
									<input id="delete_all" type="checkbox" className="form-check-input"
										onChange={(e) => setSelectedItem({ ...selectedItem, 'delete_all': e.target.checked })}
										checked={selectedItem.delete_all ?? false} />
									<label className="form-check-label text-danger fw-500" htmlFor="delete_all">
										{t('promo_turn_off_delete_all')}
									</label>
								</div>
							</>
						}
						{selectedItem.status === -1 &&
							<p className="fz-14 text-muted">{t('promo_turn_on_hint')}</p>
						}

						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalPromoCodeStatus(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL PROMO CODE STATUS*/}
		</>
	)
}

export default Index
